// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { formatDate } from "@/lib/formatDateTime"
import { CopyableId } from "@/views/forges/copyableId"

const EM_DASH = "—"

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

const renderFieldValue = (value: unknown): string => {
  // null / empty ⇒ '—' placeholder, never a fabricated value.
  if (value == null || value === "") return EM_DASH
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

// One eval-protocol entry ⇒ one compact chip label: a scalar renders as
// `key: value`, an object value is stringified compactly inside the chip.
const chipLabel = (key: string, value: unknown): string => {
  if (value == null || value === "") return `${key}: ${EM_DASH}`
  if (typeof value === "object") return `${key}: ${JSON.stringify(value)}`
  return `${key}: ${value}`
}

const specRowClass =
  "flex items-baseline gap-3 border-b border-border py-[11px] last:border-b-0"
const specKeyClass =
  "w-[130px] shrink-0 font-mono text-[11px] text-text-tertiary"

export interface ForgeSpecProps {
  forgeId: string
  createdAt: string
  specJson: Record<string, unknown>
}

export const ForgeSpec = ({ forgeId, createdAt, specJson }: ForgeSpecProps) => {
  const brief = asRecord(specJson.brief)
  const description = brief ? renderFieldValue(brief.description) : EM_DASH
  const evaluationProtocol = asRecord(specJson.evaluation_protocol)
  const protocolEntries = evaluationProtocol
    ? Object.entries(evaluationProtocol)
    : []

  return (
    <section
      role="region"
      aria-label="Forge spec and metadata"
      className="flex flex-col gap-3 border-t border-border pt-5"
    >
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-text-tertiary">
        Spec &amp; metadata
      </span>

      <div className="flex flex-wrap gap-x-6 gap-y-0">
        <div className="min-w-[260px] flex-[1.3]">
          <div className={specRowClass}>
            <span className={specKeyClass}>description</span>
            <span className="text-[12.5px] text-text-secondary">
              {description}
            </span>
          </div>
          <div className={specRowClass}>
            <span className={specKeyClass}>eval_protocol</span>
            {protocolEntries.length > 0 ? (
              <span className="flex flex-wrap gap-1.5">
                {protocolEntries.map(([key, value]) => (
                  <span
                    key={key}
                    className="inline-flex items-center rounded-md border border-border bg-surface px-2 py-0.5 font-mono text-[10.5px] text-text-secondary"
                  >
                    {chipLabel(key, value)}
                  </span>
                ))}
              </span>
            ) : (
              <span className="font-semibold text-text-muted">{EM_DASH}</span>
            )}
          </div>
        </div>

        <div className="min-w-[180px] flex-1">
          <div className={specRowClass}>
            <span className={specKeyClass}>forge_id</span>
            <span className="inline-flex items-center gap-1.5 font-mono text-[11.5px] text-text-secondary">
              <span className="select-text">{forgeId}</span>
              <CopyableId id={forgeId} label="forge id" />
            </span>
          </div>
          <div className={specRowClass}>
            <span className={specKeyClass}>created</span>
            <span className="text-[12.5px] text-text-secondary">
              {formatDate(createdAt)}
            </span>
          </div>
        </div>
      </div>
    </section>
  )
}
